import React, { useState, useEffect } from 'react';
import { CheckCircle, Circle, Clock, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils';
import { STATUS_VALUES, WORKFLOW_STEPS } from '../constants/statuses';

const StatusTimelineSimplified = ({ currentStatus, statusHistory = {} }) => {
  const [expandedPhases, setExpandedPhases] = useState({});

  // Définir les grandes phases avec leurs sous-étapes
  const phases = [
    {
      id: 'preparation',
      title: '📝 Préparation',
      description: 'Demande et validation du devis',
      steps: [
        STATUS_VALUES.DEMANDE_ENREGISTREE,
        STATUS_VALUES.EN_ATTENTE_DE_DEVIS,
        STATUS_VALUES.DEVIS_CREE,
        STATUS_VALUES.EN_ATTENTE_VALIDATION_CLIENT,
        STATUS_VALUES.VALIDEE_PLANIFICATION,
      ]
    },
    {
      id: 'execution',
      title: '🚛 Exécution',
      description: 'Transport et livraison',
      steps: [
        STATUS_VALUES.PLANIFIEE,
        STATUS_VALUES.EN_COURS_DE_CHARGEMENT,
        STATUS_VALUES.CHARGE_EN_ROUTE,
        STATUS_VALUES.LIVREE,
      ]
    },
    {
      id: 'payment',
      title: '💳 Facturation',
      description: 'Paiement de la prestation',
      steps: [
        STATUS_VALUES.EN_ATTENTE_PAIEMENT,
        STATUS_VALUES.PAYEE,
      ]
    },
    {
      id: 'closure',
      title: '✅ Clôture',
      description: 'Opération terminée',
      steps: [
        STATUS_VALUES.TERMINEE,
      ]
    }
  ];

  const getStepLabel = (step) => {
    const labels = {
      [STATUS_VALUES.DEMANDE_ENREGISTREE]: 'Demande enregistrée',
      [STATUS_VALUES.EN_ATTENTE_DE_DEVIS]: 'Analyse en cours',
      [STATUS_VALUES.DEVIS_CREE]: 'Devis créé',
      [STATUS_VALUES.EN_ATTENTE_VALIDATION_CLIENT]: 'En attente de validation',
      [STATUS_VALUES.VALIDEE_PLANIFICATION]: 'Validée',
      [STATUS_VALUES.PLANIFIEE]: 'Planifiée',
      [STATUS_VALUES.EN_COURS_DE_CHARGEMENT]: 'Chargement',
      [STATUS_VALUES.CHARGE_EN_ROUTE]: 'En route',
      [STATUS_VALUES.LIVREE]: 'Livrée',
      [STATUS_VALUES.EN_ATTENTE_PAIEMENT]: 'En attente de paiement',
      [STATUS_VALUES.PAYEE]: 'Payée',
      [STATUS_VALUES.TERMINEE]: 'Terminée',
    };
    return labels[step] || step;
  };

  const currentStepIndex = WORKFLOW_STEPS.indexOf(currentStatus);

  const getPhaseStatus = (phase) => {
    const phaseSteps = phase.steps;
    const phaseStartIndex = WORKFLOW_STEPS.indexOf(phaseSteps[0]);
    const phaseEndIndex = WORKFLOW_STEPS.indexOf(phaseSteps[phaseSteps.length - 1]);

    if (currentStepIndex > phaseEndIndex) {
      return 'completed'; // Phase terminée
    } else if (currentStepIndex >= phaseStartIndex && currentStepIndex <= phaseEndIndex) {
      return 'active'; // Phase en cours
    } else {
      return 'pending'; // Phase à venir
    }
  };

  const togglePhase = (phaseId) => {
    setExpandedPhases(prev => ({
      ...prev,
      [phaseId]: !prev[phaseId]
    }));
  };

  const isStepCompleted = (stepValue) => {
    const stepIndex = WORKFLOW_STEPS.indexOf(stepValue);
    return stepIndex < currentStepIndex || stepValue === currentStatus;
  };

  // PM Fix 17/02/2026 — rules-of-hooks : auto-expand la phase active au niveau racine
  // (au lieu d'un useEffect dans le .map() qui violait les règles React)
  useEffect(() => {
    const activePhase = phases.find(p => getPhaseStatus(p) === 'active');
    if (activePhase && !expandedPhases[activePhase.id]) {
      setExpandedPhases(prev => ({ ...prev, [activePhase.id]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStatus]);

  return (
    <div className="space-y-4">
      {phases.map((phase, phaseIndex) => {
        const phaseStatus = getPhaseStatus(phase);
        const isExpanded = expandedPhases[phase.id];
        const isCurrentPhase = phaseStatus === 'active';

        return (
          <div key={phase.id} className="relative">
            {/* Ligne de connexion */}
            {phaseIndex < phases.length - 1 && (
              <div
                className={cn(
                  'absolute left-6 top-16 w-0.5 h-12',
                  phaseStatus === 'completed' ? 'bg-green-500' : 'bg-gray-200'
                )}
              />
            )}

            {/* Carte de phase */}
            <div
              className={cn(
                'rounded-lg border-2 transition-all cursor-pointer',
                phaseStatus === 'completed' && 'border-green-500 bg-green-50',
                phaseStatus === 'active' && 'border-blue-500 bg-blue-50 shadow-md',
                phaseStatus === 'pending' && 'border-gray-200 bg-gray-50'
              )}
              onClick={() => togglePhase(phase.id)}
            >
              <div className="flex items-center gap-4 p-4">
                {/* Icône de phase */}
                <div
                  className={cn(
                    'flex items-center justify-center w-12 h-12 rounded-full flex-shrink-0',
                    phaseStatus === 'completed' && 'bg-green-500 text-white',
                    phaseStatus === 'active' && 'bg-blue-500 text-white',
                    phaseStatus === 'pending' && 'bg-gray-300 text-gray-500'
                  )}
                >
                  {phaseStatus === 'completed' ? (
                    <CheckCircle className="w-6 h-6" />
                  ) : phaseStatus === 'active' ? (
                    <Clock className="w-6 h-6" />
                  ) : (
                    <Circle className="w-6 h-6" />
                  )}
                </div>

                {/* Contenu de phase */}
                <div className="flex-1">
                  <h3
                    className={cn(
                      'text-lg font-semibold flex items-center gap-2',
                      phaseStatus === 'completed' && 'text-green-700',
                      phaseStatus === 'active' && 'text-blue-700',
                      phaseStatus === 'pending' && 'text-gray-600'
                    )}
                  >
                    {phase.title}
                    {isCurrentPhase && (
                      <span className="text-xs font-normal px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                        En cours
                      </span>
                    )}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">{phase.description}</p>
                </div>

                {/* Chevron pour expand/collapse */}
                <div className="flex-shrink-0">
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>

              {/* Sous-étapes (collapsibles) */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-2 border-t border-gray-200">
                  <div className="space-y-2 ml-12">
                    {phase.steps.map((stepValue, stepIndex) => {
                      const isCompleted = isStepCompleted(stepValue);
                      const isCurrent = stepValue === currentStatus;

                      return (
                        <div
                          key={stepValue}
                          className={cn(
                            'flex items-center gap-3 p-2 rounded-md text-sm',
                            isCurrent && 'bg-blue-100 font-medium'
                          )}
                        >
                          <div
                            className={cn(
                              'w-2 h-2 rounded-full flex-shrink-0',
                              isCompleted ? 'bg-green-500' : 'bg-gray-300'
                            )}
                          />
                          <span
                            className={cn(
                              isCompleted ? 'text-green-700' : 'text-gray-500'
                            )}
                          >
                            {getStepLabel(stepValue)}
                          </span>
                          {isCurrent && (
                            <span className="text-xs text-blue-600">← Étape actuelle</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default StatusTimelineSimplified;
